/**
 * SAM4sDirect Solution Center — the builder UI.
 *
 * Extracted from sections/sam4s-solution-builder.liquid on 2026-09-08, because
 * the section grew past the size Shopify will accept for a section file: a
 * push of a 138KB section was silently rejected and the deploy wrapper's
 * pull-back diff caught it against the 110KB copy still on the remote.
 *
 * Living here is better regardless. The browser caches it, it is not
 * re-parsed on every render, and it can be linted and read as JavaScript
 * rather than as a string inside markup. The section keeps the markup and the
 * schema; the logic that decides anything stays in sam4s-solution-core.js.
 */
import { STEPS, LIST_KEYS, PRIVATE_KEYS, encodeState, decodeState, privateState, stepIndex, stepsForPath, recommendFor, leadSummary, resultFor, buildCart, leadPayload, LEAD_LABELS }
    from 'sam4s-solution-core';
import { addLines } from 'sam4s-cart-add';
import { scannerPairFor, scannerShippingChecks } from 'sam4s-solution-core';

  const root = document.querySelector('[data-s4sb]');
  const standaloneLicense = new URLSearchParams(window.location.search).get('register') === 'sam4pos-android-software-5-year-license';
  if (root && standaloneLicense) {
    root.innerHTML = '<aside class="s4sb__panel"><h2>SAM4POS licenses are quote-only</h2><p>A standalone license is not a register or a complete system. We will verify your terminal and software before issuing a quote.</p><a class="s4sb__btn" href="/pages/contact?sam4sdirect_lead_type=New%20Register%20Sale&amp;current_pos_system_or_cash_register=SAM4POS%20license%20request%20-%20SKU%20951055&amp;message=Quote%20SAM4POS%20software-only.%20SKU%20951055.%20Product%2015666875859110.%20Variant%2057225370108070.&amp;source_page=sam4sdirect%3Aalready-own-a-sam4s-license-quote">Request a license quote</a></aside>';
    // Quote-only conversions from this legacy builder route are recorded under their own event name
    // and source_page, so historical lead data stays comparable. The page's generic link listener also
    // fires s4s_help_click (click_category lead) on the same click: never add the two together. Click only: no form, no submission, no lead is created
    // here, and the event fires at most once per page load. It borrows the shared consent-gated
    // emitter (the layout renders the attribution snippet on this page); with no emitter it is silent.
    const quoteLink = root.querySelector('a.s4sb__btn');
    let quoteClickSent = false;
    if (quoteLink) {
      quoteLink.addEventListener('click', () => {
        if (quoteClickSent) return;
        quoteClickSent = true;
        const api = window.S4SAnalytics;
        if (!api || typeof api.emit !== 'function') return;
        api.emit('s4s_license_quote_click', {
          help_page: window.location.pathname,
          entry_point: 'solution_builder',
          source_page: 'sam4sdirect:already-own-a-sam4s-license-quote',
          license_sku: '951055',
        });
      });
    }
  }
  if (root && !standaloneLicense) {
    const form = root.querySelector('[data-s4sb-form]');
    const nav = root.querySelector('[data-s4sb-nav]');
    const progress = root.querySelector('[data-s4sb-progress]');
    const stepEls = [...root.querySelectorAll('[data-s4sb-step]')];
    const present = stepEls.map((el) => el.dataset.s4sbStep);

    /* Customer-typed text is never in the URL. It is restored from
       history.state, which the browser keeps per entry and never transmits. */
    let state = { ...decodeState(window.location.search), ...privateState(history.state) };

    /* Remove a legacy link's typed values from the address bar, once. */
    if (PRIVATE_KEYS.some((k) => new URLSearchParams(window.location.search).has(k))) {
      history.replaceState(history.state, '', encodeState(state));
    }

    /*
     * Seed the path from where the builder is embedded when the URL does not
     * carry one.
     *
     * Landing on /pages/merchant-statement-review with no query string left
     * state.path undefined. Step selection coped, because it falls back to the
     * section setting - but recommendFor() reads state.path directly, so the
     * result screen showed the generic "let us put this together" instead of
     * the statement-review outcome, and the URL never gained path=D, so a
     * shared link came back as Path A. Seeding it once here keeps every
     * consumer of state.path honest rather than making each of them remember
     * the fallback.
     */
    if (!state.path && root.dataset.s4sbEntry) state.path = root.dataset.s4sbEntry;

    /*
     * Answers the page already knows.
     *
     * The scanner page knows the visitor came for scanning; a product page
     * knows which register they were looking at. Asking again is how a
     * builder feels like a form. The URL always wins, so a customer who has
     * actually answered is never overridden by the page they landed on.
     */
    if (root.dataset.s4sbPreset) {
      const preset = decodeState('?v=1&' + root.dataset.s4sbPreset);
      for (const [k, v] of Object.entries(preset)) {
        if (state[k] === undefined) state[k] = v;
      }
    }

    /* The static outline is the no-JS content. Once the builder is running it
       would be a duplicate of the questions themselves, so it steps aside -
       but only after we know the module actually loaded. */
    const staticBlock = root.querySelector('[data-s4sb-static]');
    if (staticBlock) staticBlock.hidden = true;
    nav.hidden = false;
    progress.hidden = false;

    /* Steps present in the DOM, narrowed to the ones this entry path asks. */
    function orderedSteps() {
      const forPath = stepsForPath(state.path || root.dataset.s4sbEntry);
      const usable = STEPS.filter((s) => present.includes(s) && forPath.includes(s));
      return usable.length ? usable : STEPS.filter((s) => present.includes(s));
    }

    function currentStepName() {
      const ordered = orderedSteps();
      const wanted = STEPS[stepIndex(state)];
      return present.includes(wanted) ? wanted : ordered[0];
    }

    function render({ moveFocus = false } = {}) {
      const now = currentStepName();
      for (const el of stepEls) el.hidden = el.dataset.s4sbStep !== now;

      const ordered = orderedSteps();
      const pos = ordered.indexOf(now) + 1;
      root.querySelector('[data-s4sb-step-now]').textContent = String(pos);
      root.querySelector('[data-s4sb-step-total]').textContent = String(ordered.length);
      const legend = root.querySelector('[data-s4sb-step="' + now + '"] .s4sb__legend');
      root.querySelector('[data-s4sb-step-name]').textContent =
        legend ? legend.textContent.trim().replace(/\?$/, '').toLowerCase() : now;

      const bar = root.querySelector('[data-s4sb-bar]');
      bar.setAttribute('aria-valuenow', String(pos));
      bar.setAttribute('aria-valuemax', String(ordered.length));
      root.querySelector('[data-s4sb-bar-fill]').style.width =
        Math.round((pos / ordered.length) * 100) + '%';

      root.querySelector('[data-s4sb-back]').disabled = pos === 1;

      if (now === 'result') renderResult();

      /* Tell a screen-reader user where they landed. Without this the DOM
         changes under them and nothing is announced. */
      if (moveFocus && legend) legend.focus();
    }

    /* Answers are restored from the URL so Back, a bookmark and a shared link
       all behave the same way. */
    function restore() {
      for (const [k, v] of Object.entries(state)) {
        /* Free-text answers have no value attribute to match on, so they were
           silently not restored: the URL carried the typed processor, the lead
           carried it, and a reload showed an empty box. Handled first. */
        const text = form.querySelector('input[type="text"][name="' + k + '"], textarea[name="' + k + '"]');
        if (text) { text.value = v; continue; }

        const values = Array.isArray(v) ? v : [v];
        for (const val of values) {
          const input = form.querySelector('[name="' + k + '"][value="' + CSS.escape(val) + '"]');
          if (input) input.checked = true;
        }
      }
    }

    function collect() {
      const data = new FormData(form);
      const next = { ...state };
      /* A checkbox group has to stay a list even when exactly one box is
         ticked. It used to collapse to a bare string, and every consumer that
         iterates it - readCatalog above all - then walked the characters of
         the handle and matched nothing. The customer ticked one roll of paper,
         watched the URL record it, and would have received a register with no
         paper: not priced, not carted, not listed as outstanding. decodeState
         always produces arrays for these keys, so this makes the two agree. */
      for (const key of new Set([...data.keys()])) {
        const all = data.getAll(key).filter(Boolean);
        if (!all.length) continue;
        next[key] = LIST_KEYS.includes(key) ? all : (all.length > 1 ? all : all[0]);
      }
      return next;
    }

    function go(delta) {
      const ordered = orderedSteps();
      const pos = ordered.indexOf(currentStepName());
      const target = ordered[Math.min(Math.max(pos + delta, 0), ordered.length - 1)];
      state = { ...collect(), step: target };
      history.pushState(state, '', encodeState(state));
      render({ moveFocus: true });
    }

    /** What the customer is actually asking for, in their own terms. */
    function leadAction(outcome, rec) {
      if (outcome && outcome.state === 'C') return 'Verification and a quote';
      if (rec && rec.assessment === 'fine') return 'Optional second opinion, not requested urgently';
      if (rec && rec.assessment) return 'Processing review';
      if (outcome && outcome.state === 'B') return 'Help finishing the configuration';
      return 'Specialist follow-up';
    }

    /**
     * One emitter, borrowed from the attribution snippet, so the consent check
     * and the sink list exist in exactly one place. If that snippet is not on
     * the page the builder measures nothing rather than inventing its own
     * consent policy - silence is the safe failure here.
     */
    function emit(name, payload) {
      const api = window.S4SAnalytics;
      if (!api || typeof api.emit !== 'function') return false;
      api.emit(name, Object.assign({
        help_page: (document.body && document.body.getAttribute('data-s4s-help-page')) || location.pathname,
        /* The path the customer is actually on wins over the section default.
           Reading only the section setting reported every Path B and Path D
           lead as Path A: the setting describes where the builder is embedded,
           not the journey somebody chose. */
        entry_path: state.path || root.dataset.s4sbEntry || null,
      }, payload));
      return true;
    }

    /**
     * Show the registers the catalogue says suit this business, and put the
     * rest one click away rather than out of reach. Filtering is on authored
     * business_use_cases, never on keyboard type - the SPS-340 is a flat-key
     * register mapped to retail, and a keyboard filter would hide it from
     * exactly the customer it suits.
     */
    /**
     * Some options belong to particular journeys: "a standalone card terminal"
     * is a Path C answer and would be noise in Path B. Options declare which
     * paths they apply to and the rest are removed from the tab order as well
     * as hidden - a hidden radio that is still focusable is worse than a
     * visible one.
     */
    function filterPathOptions() {
      const path = state.path || root.dataset.s4sbEntry || 'A';
      for (const input of root.querySelectorAll('input[data-paths]')) {
        const applies = (input.dataset.paths || '').split(',').includes(path);
        const label = input.closest('.s4sb__option');
        if (label) label.hidden = !applies;
        input.disabled = !applies;
        if (!applies) input.checked = false;
      }
    }

    /**
     * Show the kitchen question to food-service journeys only, reveal the
     * printer list when a printer is actually wanted, and steer the list
     * toward the right technology.
     *
     * Impact printers are shown FIRST for a kitchen because thermal paper
     * fades in heat and steam - but thermal ones are not hidden, because
     * somebody buying both a receipt printer and a kitchen printer in the same
     * pass should not have to come back for the second one.
     */
    function syncPrinterPanels() {
      const data = new FormData(form);
      const biz = (data.get('biz') || '').toString();
      const printing = (data.get('printing') || '').toString();
      const kitchen = (data.get('kitchen') || '').toString();

      const kitchenBlock = root.querySelector('[data-s4sb-kitchen-block]');
      if (kitchenBlock) kitchenBlock.hidden = biz !== 'food-service';

      const wantsKitchen = kitchen === 'one' || kitchen === 'multiple';
      const wantsExternal = printing === 'external';
      const list = root.querySelector('[data-s4sb-printer-list]');
      if (!list) return;
      list.hidden = !(wantsKitchen || wantsExternal);

      const heading = root.querySelector('[data-s4sb-printer-heading]');
      if (heading) {
        heading.textContent = wantsKitchen && !wantsExternal
          ? 'Kitchen and receipt printers we stock'
          : 'Printers we stock';
      }

      /* Impact first when a kitchen printer is the point. */
      const box = list.querySelector('.s4sb__options');
      if (box && wantsKitchen) {
        const opts = [...box.querySelectorAll('[data-s4sb-printer-option]')];
        for (const o of opts.filter((x) => x.dataset.tech === 'Impact')) box.prepend(o);
      }
    }

    /**
     * Show what the chosen register already includes, and match paper to the
     * width its printer actually takes.
     *
     * The included list is the guard against the whole class of mistake this
     * step is about: a customer who can see "cash drawer - included" is not
     * going to be sold one, and neither is the next person to edit this file.
     */
    function syncPeripherals() {
      const register = (new FormData(form).get('register') || '').toString();
      const box = root.querySelector('[data-s4sb-included]');
      const list = root.querySelector('[data-s4sb-included-list]');
      const source = root.querySelector('[data-s4sb-included-source]');
      if (!box || !list || !source) return;

      list.textContent = '';
      const found = source.querySelector('[data-for-register="' + (register ? CSS.escape(register) : '__none__') + '"]');
      if (register && found) {
        for (const span of found.querySelectorAll('[data-included-item]')) {
          const li = document.createElement('li');
          const strong = document.createElement('strong');
          strong.textContent = span.dataset.includedItem;
          li.append(strong, document.createTextNode(' — ' + span.textContent));
          list.appendChild(li);
        }
      }
      box.hidden = list.children.length === 0;

      /* Paper: a documented width on both sides is a specification match, not
         a compatibility claim, so it can be stated plainly. Non-matching
         widths stay visible - somebody may be buying for a second machine. */
      const regOpt = root.querySelector('[data-s4sb-register-option] input[value="' + (register ? CSS.escape(register) : '__none__') + '"]');
      const width = regOpt ? regOpt.closest('[data-s4sb-register-option]').dataset.paper : '';
      for (const opt of root.querySelectorAll('[data-s4sb-paper-option]')) {
        const badge = opt.querySelector('[data-s4sb-paper-badge]');
        if (badge) badge.hidden = !(width && opt.dataset.width === width);
      }
      /* Accessories, from the register's own authored relationships. */
      const accBox = root.querySelector('[data-s4sb-accessories]');
      const accList = root.querySelector('[data-s4sb-accessory-list]');
      const accSource = root.querySelector('[data-s4sb-accessory-source]');
      if (accBox && accList && accSource) {
        accList.textContent = '';
        const forReg = accSource.querySelector('[data-for-register="' + (register ? CSS.escape(register) : '__none__') + '"]');
        if (register && forReg) {
          for (const sp of forReg.querySelectorAll('[data-acc-handle]')) {
            const label = document.createElement('label');
            label.className = 's4sb__option';
            label.setAttribute('data-s4sb-accessory-option', '');
            label.dataset.status = sp.dataset.accStatus;
            label.dataset.type = sp.dataset.accType;

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.name = 'peripherals';
            input.value = sp.dataset.accHandle;

            const body = document.createElement('span');
            body.className = 's4sb__option-body';
            const title = document.createElement('span');
            title.className = 's4sb__option-title';
            title.textContent = sp.dataset.accTitle + ' · ' + sp.dataset.accPrice;
            const type = document.createElement('span');
            type.className = 's4sb__option-note';
            type.textContent = sp.dataset.accType;
            body.append(title, type);

            /*
             * The subtler version of the cash-drawer mistake.
             *
             * These registers already include a rear customer display, so a
             * pole display is an UPGRADE to something present rather than a
             * missing part. Selling it as the latter would repeat the drawer
             * error somewhere far less obvious, so the note is derived from
             * what this register actually includes rather than assumed.
             */
            const includesDisplay = [...list.querySelectorAll('strong')]
              .some((el) => /display/i.test(el.textContent));
            if (includesDisplay && /display/i.test(sp.dataset.accType)) {
              const note = document.createElement('span');
              note.className = 's4sb__option-note';
              const b = document.createElement('strong');
              b.textContent = 'An upgrade, not a missing part';
              note.append(b, document.createTextNode(
                ' — your register already includes a customer display.'));
              body.appendChild(note);
            }

            const badge = document.createElement('span');
            badge.className = 's4sb__badge s4sb__badge--unresolved';
            badge.textContent = sp.dataset.accStatus;
            body.appendChild(badge);
            label.append(input, body);
            accList.appendChild(label);
          }
        }
        /* An empty heading reads as broken data rather than as absent data. */
        accBox.hidden = accList.children.length === 0;
      }

      const note = root.querySelector('[data-s4sb-paper-note]');
      if (note && width) {
        note.textContent = 'Your register takes ' + width + ' paper. The matching rolls are marked.';
      }
    }

    /**
     * Show the programming detail only once it is wanted, and show only the
     * items the rest of the build has made relevant: barcode items when a
     * scanner is in play, menu keys for food service, kitchen routing only
     * when a kitchen printer was actually asked for.
     */
    function syncProgramming() {
      const data = new FormData(form);
      const intent = (data.get('programmingIntent') || '').toString();
      const scanner = (data.get('scanner') || '').toString();
      const kitchen = (data.get('kitchen') || '').toString();
      const biz = (data.get('biz') || '').toString();

      const needs = root.querySelector('[data-s4sb-programming-needs]');
      if (needs) needs.hidden = intent !== 'yes';

      const wantsScanner = scanner === 'yes' || scanner === 'have-one';
      const barcode = root.querySelector('[data-s4sb-prog-barcode]');
      if (barcode) barcode.hidden = !wantsScanner;
      const scannerNote = root.querySelector('[data-s4sb-prog-scannernote]');
      if (scannerNote) scannerNote.hidden = !wantsScanner;

      const menu = root.querySelector('[data-s4sb-prog-menu]');
      if (menu) menu.hidden = biz !== 'food-service';

      /* Only offered when a kitchen printer was actually requested, and even
         then it carries its own verification badge. */
      const routing = root.querySelector('[data-s4sb-prog-routing]');
      if (routing) routing.hidden = !(kitchen === 'one' || kitchen === 'multiple');
    }

    /** Keep the scanner panels consistent with the restored answer. */
    function syncScannerPanels() {
      const chosen = (new FormData(form).get('scanner') || '').toString();
      const list = root.querySelector('[data-s4sb-scanner-list]');
      const owned = root.querySelector('[data-s4sb-scanner-owned]');
      if (list) list.hidden = chosen !== 'yes';
      if (owned) owned.hidden = chosen !== 'have-one';
      syncVerifiedScanner();
    }

    /**
     * The verified scanner pairs the page supplied (setting-gated: the section renders none
     * unless "Use verified scanner pairs" is ticked). Plain data read from attributes; whether a
     * pair APPLIES is decided by scannerPairFor in the core, never here.
     */
    function pairsFromDom() {
      return [...root.querySelectorAll('[data-s4sb-scanner-pairs] [data-pair]')].map((el) => ({
        register: el.dataset.register,
        scanner: el.dataset.scanner,
        sku: el.dataset.sku,
        variantId: el.dataset.variant,
        cents: Number(el.dataset.cents || 0),
        title: el.dataset.title,
        interface: el.dataset.interface,
        power: el.dataset.power,
        available: true,
      }));
    }

    /**
     * Mark the scanner verified for the chosen register, and say what a person still confirms.
     * Reads the form (not `state`), because this runs while the customer is still on the step.
     * With no pairs on the page (the default) it only restores the original badges and hides the card.
     */
    function syncVerifiedScanner() {
      const card = root.querySelector('[data-s4sb-scanner-verified]');
      const options = [...root.querySelectorAll('[data-s4sb-scanner-option]')];
      for (const opt of options) {
        const badge = opt.querySelector('.s4sb__badge');
        if (badge && badge.dataset.orig !== undefined) {
          badge.textContent = badge.dataset.orig;
          badge.className = 's4sb__badge s4sb__badge--unresolved';
        }
      }
      if (card) { card.hidden = true; card.textContent = ''; }

      const pairs = pairsFromDom();
      if (!pairs.length) return;
      const data = new FormData(form);
      const register = (data.get('register') || '').toString();
      const wantsList = (data.get('scanner') || '').toString() === 'yes';
      const mine = pairs.filter((p) => p.register === register);
      if (!register || !mine.length) return;

      for (const p of mine) {
        const input = root.querySelector('[data-s4sb-scanner-option] input[value="' + CSS.escape(p.scanner) + '"]');
        const opt = input ? input.closest('[data-s4sb-scanner-option]') : null;
        const badge = opt ? opt.querySelector('.s4sb__badge') : null;
        if (!opt || !badge) continue;
        if (badge.dataset.orig === undefined) badge.dataset.orig = badge.textContent;
        badge.textContent = 'Verified for your register';
        badge.className = 's4sb__badge s4sb__badge--matched';
      }
      if (card && wantsList) {
        const p = mine[0];
        const h = document.createElement('h3');
        h.className = 's4sb__subhead';
        h.textContent = 'Verified for your register';
        const line = document.createElement('p');
        line.textContent = p.title + (p.interface && p.interface !== 'Confirm' ? ' (' + p.interface + ')' : '') + ' · ' + money(p.cents) + '. Choose it below to add it to your order.';
        card.append(h, line);
        for (const c of scannerShippingChecks(p)) {
          const note = document.createElement('p');
          note.textContent = c;
          card.appendChild(note);
        }
        card.hidden = false;
      }
    }

    /** Human labels for the summary, so a lead does not read like a form dump. */
    /*
     * The screen and the lead read from the same labels, so what the customer
     * is shown and what the specialist receives cannot drift apart.
     *
     * The local copy this replaces had "current" where the field is actually
     * called "currentRegister", and no entry at all for "prepLocations". Both
     * still reached the lead, because leadSummary falls back to the raw key -
     * so Path B, whose entire subject is the register somebody already owns,
     * sent a line reading "currentRegister: er-390m".
     */
    const SUMMARY_LABELS = LEAD_LABELS;

    const money = (cents) => '$'
 + (cents / 100).toFixed(2);

    /** Everything needed to price and cart the current configuration. */
    function readCatalog() {
      const pick = (sel, value) => root.querySelector(sel + ' input[value="' + CSS.escape(value) + '"]');
      const cat = { optionalUnresolved: [], supplyVariants: [], spareVariants: [], lines: [], included: [] };

      const regInput = state.register ? pick('[data-s4sb-register-option]', state.register) : null;
      const reg = regInput ? regInput.closest('[data-s4sb-register-option]') : null;
      if (reg) {
        cat.registerVariant = reg.dataset.variant;
        cat.registerTitle = reg.dataset.title;
        cat.lines.push({ title: reg.dataset.title, cents: Number(reg.dataset.cents || 0), url: reg.dataset.url });
      }

      /* Included items come from the register's own data and never priced. */
      for (const li of root.querySelectorAll('[data-s4sb-included-list] li')) {
        cat.included.push(li.textContent);
      }

      for (const v of (state.supplies || [])) {
        const el = pick('[data-s4sb-paper-option]', v);
        const opt = el ? el.closest('[data-s4sb-paper-option]') : null;
        if (!opt) continue;
        cat.supplyVariants.push(opt.dataset.variant);
        cat.lines.push({ title: opt.dataset.title, cents: Number(opt.dataset.cents || 0) });
      }

      for (const v of (state.peripherals || [])) {
        const spareEl = pick('[data-s4sb-spare-option]', v);
        if (spareEl) {
          const opt = spareEl.closest('[data-s4sb-spare-option]');
          cat.spareVariants.push(opt.dataset.variant);
          cat.lines.push({ title: opt.dataset.title, cents: Number(opt.dataset.cents || 0) });
          continue;
        }
        /* An accessory with an unresolved status is listed, never carted. */
        const accEl = root.querySelector('[data-s4sb-accessory-option] input[value="' + CSS.escape(v) + '"]');
        const acc = accEl ? accEl.closest('[data-s4sb-accessory-option]') : null;
        if (acc) {
          cat.optionalUnresolved.push({
            title: acc.querySelector('.s4sb__option-title').textContent,
            why: acc.dataset.status,
          });
        }
      }

      /* A verified scanner pair, when the page supplies pairs AND the customer chose that exact
         register and scanner. Priced and listed like any other line; the cart line itself is built
         (and re-checked) by buildCart from the pinned variant. */
      cat.scannerPairs = pairsFromDom();
      const pair = scannerPairFor(state, cat);
      if (pair) cat.lines.push({ title: pair.title + (pair.interface && pair.interface !== 'Confirm' ? ' (' + pair.interface + ')' : ''), cents: pair.cents });

      cat.reference = (state.register || '') + '/' + (state.biz || '');
      return cat;
    }

    function renderResult() {
      const wrap = root.querySelector('[data-s4sb-result]');
      if (!wrap) return;
      /* The catalogue (including any verified scanner pair) is read first so the recommendation
         and the order are decided from the same facts. */
      const isPathA = (state.path || 'A') === 'A';
      const catA = isPathA ? readCatalog() : null;
      const rec = recommendFor(state, catA || {});

      root.querySelector('[data-s4sb-result-state]').textContent = rec.headline;

      const body = root.querySelector('[data-s4sb-result-body]');
      body.textContent = '';
      if (rec.body) {
        const p = document.createElement('p');
        p.textContent = rec.body;
        body.appendChild(p);
      }
      if (rec.depends_on && rec.depends_on.length) {
        const h = document.createElement('h3');
        h.className = 's4sb__subhead';
        h.textContent = 'What decides the answer';
        const ul = document.createElement('ul');
        for (const item of rec.depends_on) {
          const li = document.createElement('li');
          li.textContent = item;
          ul.appendChild(li);
        }
        body.append(h, ul);
      }
      if (rec.no_promises && rec.no_promises.length) {
        const h = document.createElement('h3');
        h.className = 's4sb__subhead';
        h.textContent = 'What we are not going to claim';
        const ul = document.createElement('ul');
        for (const item of rec.no_promises) {
          const li = document.createElement('li');
          li.textContent = item;
          ul.appendChild(li);
        }
        body.append(h, ul);
      }

      /* The answers, shown back so the customer can see what will be sent. */
      const dl = root.querySelector('[data-s4sb-summary]');
      dl.textContent = '';
      for (const [key, label] of Object.entries(SUMMARY_LABELS)) {
        const v = state[key];
        if (!v) continue;
        const dt = document.createElement('dt');
        dt.textContent = label;
        const dd = document.createElement('dd');
        dd.textContent = Array.isArray(v) ? v.join(', ') : v;
        dl.append(dt, dd);
      }

      /* --- Order, pricing and the buy action (Path A only) --- */
      const orderBox = root.querySelector('[data-s4sb-order]');
      const pendingBox = root.querySelector('[data-s4sb-pending]');
      const addBtn = root.querySelector('[data-s4sb-addcart]');
      const whyEl = root.querySelector('[data-s4sb-result-why]');
      let outcome = null;

      if (isPathA) {
        const cat = catA;
        outcome = resultFor(state, cat);
        if (whyEl) whyEl.textContent = outcome.reason;

        /* Included first, and with no price. Charging for the cash drawer or
           the rear customer display would bill somebody twice for what the
           register already contains. */
        const incUl = root.querySelector('[data-s4sb-order-included]');
        incUl.textContent = '';
        for (const item of cat.included) {
          const li = document.createElement('li');
          li.textContent = item + ' — included';
          incUl.appendChild(li);
        }

        const linesUl = root.querySelector('[data-s4sb-order-lines]');
        linesUl.textContent = '';
        let subtotal = 0;
        for (const line of cat.lines) {
          subtotal += line.cents;
          const li = document.createElement('li');
          li.textContent = line.title + ' — ' + money(line.cents);
          linesUl.appendChild(li);
        }
        root.querySelector('[data-s4sb-subtotal]').textContent =
          cat.lines.length ? 'Subtotal: ' + money(subtotal) : '';

        /* Programming is not a cart line. Scope decides the price and nobody
           knows the scope yet, so a guessed block of time would be a number
           the customer never agreed to. */
        const quoted = root.querySelector('[data-s4sb-quoted]');
        quoted.hidden = state.programmingIntent !== 'yes';
        quoted.textContent = 'Programming: quoted separately once we know the scope. It is not in the subtotal above.';

        /* What a person still confirms before a VERIFIED scanner pairing ships (the cable on a
           serial pairing). Stated, not hidden: a verified label is not "plug and play". */
        const confirmEl = root.querySelector('[data-s4sb-confirm]');
        if (confirmEl) {
          const checks = outcome.confirm_before_shipping || [];
          confirmEl.hidden = !checks.length;
          confirmEl.textContent = checks.join(' ');
        }

        orderBox.hidden = !outcome.base_sellable || !cat.lines.length;

        const pendingUl = root.querySelector('[data-s4sb-pending-list]');
        pendingUl.textContent = '';
        for (const item of [...outcome.critical, ...outcome.optional_unresolved]) {
          const li = document.createElement('li');
          li.textContent = (item.what || item.title) + ' — ' + (item.why || 'needs verification');
          pendingUl.appendChild(li);
        }
        pendingBox.hidden = pendingUl.children.length === 0;

        /* The buy action exists only when the base is genuinely sellable. */
        if (addBtn) {
          addBtn.hidden = !(outcome.base_sellable && cat.lines.length);
          addBtn.textContent = outcome.state === 'B' ? 'Add this configuration to cart' : 'Add to cart';
          addBtn.onclick = () => addToCart(cat, outcome.state);
        }
      } else {
        if (whyEl) whyEl.textContent = '';
        if (orderBox) orderBox.hidden = true;
        if (pendingBox) pendingBox.hidden = true;
        if (addBtn) addBtn.hidden = true;
      }

      /* Lead handoff. Typed fields where HubSpot has them, and the whole
         configuration in message, which is the param guaranteed to arrive. */
      const lead = root.querySelector('[data-s4sb-lead]');
      if (lead) {
        /*
         * The lead type used to be the constant 'Merchant Processing
         * Interest' for every journey, which meant a Path A register purchase
         * arrived in HubSpot as a processing enquiry. That field drives
         * ownership, so it was not a cosmetic mislabel.
         */
        const base = lead.getAttribute('href').split('?')[0];
        const payload = leadPayload(state, {
          resultState: outcome ? outcome.state : null,
          critical: outcome ? outcome.critical : [],
          sourcePage: (document.body && document.body.getAttribute('data-s4s-help-page'))
            || location.pathname,
          nextAction: leadAction(outcome, rec),
          assessment: rec.assessment || null,
          recommendation: rec.headline || null,
        });
        const p = new URLSearchParams();
        for (const [k, v] of Object.entries(payload)) if (v) p.set(k, v);
        lead.setAttribute('href', base + '?' + p.toString());
        lead.dataset.s4sbLeadType = payload.sam4sdirect_lead_type;

        /* Measured on the click, because an href that exists is not a handoff
           anybody made. Intent category only - never the summary text, which
           carries free-text answers the customer typed. */
        lead.onclick = () => emit('s4s_builder_lead_cta', {
          cta: outcome && outcome.state === 'C' ? 'verification'
            : (rec.assessment ? 'statement_review' : 'quote'),
          result_state: outcome ? outcome.state : null,
          intent: payload.sam4sdirect_lead_type,
          processing_interest: payload.sam4sdirect_merchant_processing_interest,
          unresolved: outcome ? outcome.critical.map((c) => c.key) : [],
        });
      }

      /* When the answer is "nothing needs to change", the review is offered
         and not pressed. A page that says "you are fine" and then shouts at
         you to book a call has not said "you are fine". */
      const lead2 = root.querySelector('[data-s4sb-lead]');
      if (lead2 && rec.assessment === 'fine') {
        lead2.textContent = 'Have us look anyway, if you like';
        lead2.classList.remove('s4sb__btn--primary');
        lead2.classList.add('s4sb__btn--ghost');
      } else if (lead2) {
        lead2.classList.add('s4sb__btn--primary');
        lead2.classList.remove('s4sb__btn--ghost');
      }

      /*
       * Handoff into the existing programming worksheet.
       *
       * /pages/sam4s-register-onboarding already accepts prefill parameters -
       * model, project_type, old, order, business, contact, email, phone - and
       * already asks the right questions about transferring an old program.
       * Linking into it with what we know beats rebuilding an intake that
       * would drift from it within a month.
       */
      const prog = root.querySelector('[data-s4sb-onboarding]');
      if (prog) {
        const wants = state.programmingIntent === 'yes';
        prog.hidden = !wants;
        if (wants) {
          const q = new URLSearchParams();
          const regOpt = root.querySelector('[data-s4sb-register-option] input[value="' + (state.register ? CSS.escape(state.register) : '__none__') + '"]');
          const model = regOpt ? regOpt.closest('[data-s4sb-register-option]').dataset.model : '';
          if (model) q.set('model', model);
          q.set('project_type', state.currentRegister ? 'Replace an older register' : 'New register programming');
          prog.setAttribute('href', '/pages/sam4s-register-onboarding?' + q.toString());
          prog.onclick = () => emit('s4s_builder_specialist_handoff', {
            cta: 'programming_worksheet',
            result_state: outcome ? outcome.state : null,
            intent: 'Programming',
            model: model || null,
          });
        }
      }

      emit('s4s_builder_result', {
        result_state: outcome ? outcome.state : rec.state,
        result_kind: rec.kind,
        assessment: rec.assessment || null,
        sellable: outcome ? outcome.sellable : rec.sellable,
        base_sellable: outcome ? outcome.base_sellable : null,
        critical_unresolved: outcome ? outcome.critical.map((x) => x.key) : [],
        optional_unresolved: outcome ? outcome.optional_unresolved.length : 0,
      });
    }

    function filterRegisters() {
      const biz = (new FormData(form).get('biz') || '').toString();
      const list = root.querySelector('[data-s4sb-register-list]');
      const more = root.querySelector('[data-s4sb-register-more]');
      const rest = root.querySelector('[data-s4sb-register-rest]');
      if (!list || !more || !rest) return;

      /* Put everything back before re-sorting, so changing the answer on the
         way back through does not leave options stranded in the wrong group. */
      for (const el of [...rest.children]) list.appendChild(el);

      if (!biz) { more.hidden = true; return; }

      let matched = 0;
      for (const opt of [...list.querySelectorAll('[data-s4sb-register-option]')]) {
        const ucs = (opt.dataset.uc || '').split(',').filter(Boolean);
        if (ucs.includes(biz)) { matched += 1; } else { rest.appendChild(opt); }
      }

      more.hidden = rest.children.length === 0;
      const help = root.querySelector('[data-s4sb-register-help]');
      if (help) {
        help.textContent = matched
          ? 'These are the current models the catalogue lists for that business type. Prices are the real ones, and every model links to its full specifications.'
          : 'We have not mapped a model to that business type, so here is the full current range.';
      }
      if (!matched) for (const el of [...rest.children]) list.appendChild(el);
    }

    form.addEventListener('change', (ev) => {
      const t = ev.target;
      if (!t || !t.name) return;
      if (t.name === 'biz') {
        filterRegisters();
        emit('s4s_builder_business_type', { business_type: t.value });
      }
      if (t.name === 'currentRegister') {
        const panel = root.querySelector('[data-s4sb-replacement]');
        if (panel) {
          const blocks = [...panel.querySelectorAll('[data-s4sb-replacement-for]')];
          for (const bl of blocks) bl.hidden = bl.dataset.s4sbReplacementFor !== t.value;
          panel.hidden = !blocks.some((bl) => !bl.hidden);
        }
        emit('s4s_builder_current_register', {
          current_register: t.value,
          identified: t.value !== 'unknown' && t.value !== 'other',
        });
      }
      if (t.name === 'goal') {
        emit('s4s_builder_goal', { goal: t.value });
      }
      if (t.name === 'setup') {
        emit('s4s_builder_setup', { setup: t.value });
      }
      if (t.name === 'monthlyVolume' || t.name === 'averageTicket' || t.name === 'pricingModel') {
        /* Bands and categories only. No free-text figure is collected here and
           nothing from a statement is ever put in an analytics payload. */
        emit('s4s_builder_review_detail', { field: t.name, value: t.value });
      }
      if (t.name === 'programmingIntent' || t.name === 'programming') {
        syncProgramming();
        if (t.name === 'programmingIntent') {
          emit('s4s_builder_programming', { programming_intent: t.value });
        } else {
          const chosen = [...form.querySelectorAll('input[name="programming"]:checked')].map((i) => i.value);
          emit('s4s_builder_programming_needs', {
            needs: chosen,
            count: chosen.length,
            /* Selecting routing is a request, never a statement that the
               register supports it. */
            kitchen_routing_requested: chosen.includes('kitchen-routing'),
            kitchen_routing_verified: false,
          });
        }
      }
      if (t.name === 'peripherals' || t.name === 'supplies') {
        const chosen = [...form.querySelectorAll('input[name="' + t.name + '"]:checked')].map((i) => i.value);
        const opt = t.closest('[data-s4sb-accessory-option]');
        emit('s4s_builder_accessory', {
          group: t.name,
          selected: chosen,
          count: chosen.length,
          /* Never true for anything in this step. The included items are
             rendered as a list, not as selectable options, so a required
             add-on cannot be emitted from here at all. */
          required_add_on: false,
          verification_required: opt ? opt.dataset.status === 'Verification required' : false,
        });
      }
      if (t.name === 'printing' || t.name === 'kitchen') {
        syncPrinterPanels();
        if (t.name === 'printing') {
          emit('s4s_builder_printer', { external_receipt: t.value });
        } else {
          emit('s4s_builder_kitchen_print', {
            kitchen: t.value,
            /* Never assumed. Routing to more than one prep area is a
               capability claim about the register, not about the printer. */
            routing_claimed: false,
            verification_required: t.value === 'one' || t.value === 'multiple' || t.value === 'not-sure',
          });
        }
      }
      if (t.name === 'printerModel') {
        const opt = t.closest('[data-s4sb-printer-option]');
        emit('s4s_builder_printer_model', {
          printer_model: t.value,
          technology: opt ? opt.dataset.tech : null,
          status: opt ? opt.dataset.status : null,
        });
      }
      if (t.name === 'scanner') {
        const list = root.querySelector('[data-s4sb-scanner-list]');
        const owned = root.querySelector('[data-s4sb-scanner-owned]');
        if (list) list.hidden = t.value !== 'yes';
        if (owned) owned.hidden = t.value !== 'have-one';
        emit('s4s_builder_scanner', {
          scanner: t.value,
          /* Always true when a scanner is in play: the rules treat scanning as needing a
             check of the exact register and scanner. (Some pairings are verified in the
             evidence; widening the rules to use them is an owner decision, see
             docs/audits/2026-10-26-configurator-rule-review.md.) Emitted so the share of
             journeys that stall on this is measurable rather than assumed. */
          verification_required: t.value === 'yes' || t.value === 'have-one',
        });
      }
      if (t.name === 'scannerModel') {
        emit('s4s_builder_scanner_model', { scanner_model: t.value });
        const picked = scannerPairFor({ scanner: 'yes', register: new FormData(form).get('register'), scannerModel: t.value }, { scannerPairs: pairsFromDom() });
        if (picked) emit('s4s_builder_scanner_pair', { verified_pair: true, pair_interface: /usb/i.test(picked.interface) ? 'usb' : 'serial' });
      }
      if (t.name === 'scanner' || t.name === 'register' || t.name === 'scannerModel') syncVerifiedScanner();
      if (t.name === 'pain') {
        const chosen = [...form.querySelectorAll('input[name="pain"]:checked')].map((i) => i.value);
        emit('s4s_builder_pain', {
          pain: chosen,
          count: chosen.length,
          says_fine: chosen.includes('working-fine'),
        });
      }
      if (t.name === 'priority') {
        emit('s4s_builder_priority', { priority: t.value });
      }
      if (t.name === 'statementOpen') {
        emit('s4s_builder_statement_open', { statement_open: t.value });
      }
      if (t.name === 'reviewGoal') {
        emit('s4s_builder_review_goal', { review_goal: t.value });
      }
      if (t.name === 'processing') {
        emit('s4s_builder_processing', {
          processing_interest: t.value,
          wants_review: t.value === 'statement-review' || t.value === 'open-to-review',
        });
      }
      if (t.name === 'register') {
        syncPeripherals();
        const opt = t.closest('[data-s4sb-register-option]');
        emit('s4s_builder_register', {
          register: t.value,
          model: opt ? opt.dataset.model : null,
          beyond_ecr: opt ? opt.dataset.sap === 'true' : false,
        });
      }
    });

    /**
     * Add the configuration to the Shopify cart.
     *
     * Only real variants go in. There is no placeholder line for anything
     * unverified - a cart line is a thing we are willing to sell, and a line
     * called "scanner (to be confirmed)" would be a promise we have not made.
     */
    /* resultState is passed in rather than guessed. The button belongs to an
       outcome that has already been rendered, and reporting a literal "A/B"
       makes it impossible to tell a clean buy from a configured one. */
    async function addToCart(cat, resultState) {
      const payload = buildCart(state, cat);
      if (!payload.lines.length) return;
      const btn = root.querySelector('[data-s4sb-addcart]');
      const cartError = root.querySelector('[data-s4sb-cart-error]');
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      if (cartError) cartError.hidden = true;

      const res = await addLines(payload.lines, payload.attributes);

      if (res.ok) {
        emit('s4s_builder_add_to_cart', {
          lines: payload.lines.length,
          register: state.register || null,
          result_state: resultState || null,
        });
        emit('s4s_builder_add_to_cart_result', { ok: true, lines: payload.lines.length });
        window.location.href = '/cart';
        return;
      }

      /*
       * Reached where the old code silently succeeded. fetch resolves on a 422,
       * so a Shopify refusal used to run straight past the catch, report a sale
       * and navigate to a cart that did not have the products in it.
       */
      emit('s4s_builder_add_to_cart_result', { ok: false, status: res.status });
      if (btn) { btn.disabled = false; btn.textContent = 'Add to cart'; }
      if (cartError) {
        cartError.textContent = 'That could not be added just now. Nothing has been charged, and you can try again or ask us to put the package together for you.';
        cartError.hidden = false;
      }
    }

    root.querySelector('[data-s4sb-next]').addEventListener('click', () => go(1));
    root.querySelector('[data-s4sb-back]').addEventListener('click', () => go(-1));

    /* The browser back button is a modify button here, so honour it. */
    window.addEventListener('popstate', (event) => {
      state = { ...decodeState(window.location.search), ...privateState(event.state) };
      restore();
      render();
    });

    restore();
    filterPathOptions();
    filterRegisters();
    syncScannerPanels();
    syncPrinterPanels();
    syncPeripherals();
    syncProgramming();
    render();

    /* Fired after the engine is live, not on parse - the whole point of the
       attribution incident was that existing code is not running code. */
    emit('s4s_builder_start', { step: currentStepName() });
  }
